import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import { RFQ } from '../types';
import { voiceAppCommandBus } from '../services/VoiceAppCommandBus';
import { setUploadedFiles, setAnalysisData } from '../store/rfqSlice';
import { trpClient } from '../client/trpClient';
import { UploadRow } from '../components/forms/upload/UploadFileRow';
import { useBomPricing } from './useBomPricing';
import { useRecordRobbieAction } from '../contexts/RobbieUiContext';
import { robbieActionText } from '../utils/robbieActivity';
import { ExtractionSummary } from '../components/forms/upload/UploadCreditsNote';
import { useWizardToast } from '../components/wizard/WizardToast';
import { extractionDoneToast } from '../utils/waitCopy';
import {
  ACCEPTED_TYPES, MAX_UPLOAD_FILES, MAX_UPLOAD_TOTAL_BYTES, overTotalMessage, totalBytes,
} from '../utils/uploadLimits';

/**
 * Wizard Step 1 — owns the upload logic the old UploadDocuments component
 * carried inline: existing-document fetch, dropzone upload (with real batch
 * progress), remove, and the Continue-time BOM extraction. The component
 * stays presentational (UploadStepPanel). Behavior matches the pre-restyle
 * flow; true per-file cross-referencing arrives with the later multi-file
 * datasheet work.
 */

interface UploadItem {
  file?: File;
  fileName?: string;
  fileSize?: number;
  documentId?: string;
  status: 'uploading' | 'uploaded' | 'error';
  /** 0–100 real batch upload progress (axios onUploadProgress). */
  progress?: number;
  error?: string;
}

const itemName = (item: UploadItem) => item.file?.name || item.fileName || 'Document';
const itemSize = (item: UploadItem) => item.file?.size ?? item.fileSize ?? 0;
const isBomLike = (name: string) => /\.(xlsx|xls|csv)$/i.test(name);

export const useUploadStep = (rfq: RFQ) => {
  const dispatch = useDispatch();
  const [fileItems, setFileItems] = useState<UploadItem[]>([]);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionError, setExtractionError] = useState<string | null>(null);
  // The extraction itself failed (not a file still uploading): the overlay
  // shows the error in place until the buyer retries or goes back to files.
  const [extractionFailed, setExtractionFailed] = useState(false);
  const showToast = useWizardToast();
  // Count from the extraction run in THIS session (rfq prop may lag behind).
  const [sessionComponentsFound, setSessionComponentsFound] = useState(0);
  // What this session's extraction produced. Extraction is free — the BOM is
  // charged when the buyer starts discovery on step 2 — so this is a count,
  // never a charge.
  const [extraction, setExtraction] = useState<ExtractionSummary | null>(null);
  const pricing = useBomPricing();
  const recordAction = useRecordRobbieAction();

  const selectedFiles = useMemo(
    () => fileItems.map(i => i.file || ({ name: itemName(i), size: itemSize(i), type: '' } as any)),
    [fileItems]
  );

  // Re-hydrate documents already on the RFQ (revisit / refresh).
  useEffect(() => {
    const fetchExistingDocuments = async () => {
      if (!rfq.rfqId) return;
      try {
        const docs = await trpClient.getDocuments(rfq.rfqId);
        if (docs && docs.length > 0) {
          const loadedItems: UploadItem[] = docs.map((d: any) => ({
            fileName: d.fileName || d.originalname || 'Document',
            fileSize: d.sizeBytes || d.size || 0,
            documentId: d.documentId || d._id || d.id,
            status: d.status === 'FAILED' ? 'error' : 'uploaded',
            error: d.status === 'FAILED' ? 'Upload or processing failed' : undefined,
          }));
          setFileItems(prev => {
            const existingIds = new Set(prev.map(p => p.documentId).filter(Boolean));
            const newDocs = loadedItems.filter(l => !l.documentId || !existingIds.has(l.documentId));
            return [...prev, ...newDocs];
          });
        }
      } catch (err) {
        console.error('Failed to load existing documents:', err);
      }
    };
    fetchExistingDocuments();
  }, [rfq.rfqId]);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;
      const remainingSlots = MAX_UPLOAD_FILES - fileItems.length;
      const filesToProcess = acceptedFiles.slice(0, Math.max(0, remainingSlots));
      if (filesToProcess.length === 0) return;

      // The step's files together stay within one upload's total, so none is
      // refused by the server after it has been sent.
      const alreadyAdded = totalBytes(fileItems.filter(i => i.status !== 'error').map(itemSize));
      if (alreadyAdded + totalBytes(filesToProcess.map(f => f.size)) > MAX_UPLOAD_TOTAL_BYTES) {
        setFileItems(prev => [
          ...prev,
          ...filesToProcess.map(f => ({ file: f, status: 'error' as const, error: overTotalMessage() })),
        ]);
        return;
      }

      const newItems: UploadItem[] = filesToProcess.map(f => ({ file: f, status: 'uploading', progress: 0 }));
      setFileItems(prev => [...prev, ...newItems]);

      const allFiles = [...selectedFiles, ...filesToProcess];
      voiceAppCommandBus.sendVoiceFeedback('filesSelected', {
        count: allFiles.length,
        names: allFiles.map(f => f.name),
        totalSize: allFiles.reduce((sum, f) => sum + f.size, 0),
      });
      voiceAppCommandBus.updateContext('uploadedFiles', allFiles);

      const inBatch = (item: UploadItem) => !!item.file && filesToProcess.some(f => f === item.file);
      try {
        const uploadedDocs = await trpClient.uploadDocuments(rfq.rfqId, filesToProcess, percent => {
          setFileItems(prev =>
            prev.map(item => (inBatch(item) && item.status === 'uploading' ? { ...item, progress: percent } : item))
          );
        });
        setFileItems(prev =>
          prev.map(item => {
            let docMatch = uploadedDocs.find(
              (d: any) => (d.fileName || d.originalname || d.name || '').toLowerCase() === itemName(item).toLowerCase()
            );
            if (!docMatch && item.file) {
              const idx = filesToProcess.findIndex(f => f === item.file || f.name === item.file?.name);
              if (idx !== -1 && uploadedDocs[idx]) docMatch = uploadedDocs[idx];
            }
            if (docMatch && item.status === 'uploading') {
              return { ...item, documentId: docMatch.documentId || docMatch._id || docMatch.id, status: 'uploaded' };
            }
            return item;
          })
        );
        recordAction('upload', robbieActionText.uploaded(filesToProcess.map(f => f.name)));
      } catch (error: any) {
        const errMsg = error.response?.data?.error || error.message || 'Upload failed';
        setFileItems(prev =>
          prev.map(item => (inBatch(item) && item.status === 'uploading' ? { ...item, status: 'error', error: errMsg } : item))
        );
      }
    },
    [fileItems, selectedFiles, rfq.rfqId, recordAction]
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: ACCEPTED_TYPES,
    multiple: true,
    maxFiles: MAX_UPLOAD_FILES,
    maxSize: MAX_UPLOAD_TOTAL_BYTES,
  });

  // Voice command bus wiring (unchanged behavior from the pre-restyle step).
  useEffect(() => {
    voiceAppCommandBus.registerAppCommand('openFilePicker', async () => {
      open();
      return { success: true, message: 'File picker opened' };
    });
    voiceAppCommandBus.registerAppCommand('getSelectedFiles', async () => ({
      success: true,
      data: {
        count: selectedFiles.length,
        files: selectedFiles.map(f => ({ name: f.name, size: f.size, type: f.type })),
      },
    }));
    voiceAppCommandBus.updateContext('currentStep', 1);
    voiceAppCommandBus.updateContext('uploadedFiles', selectedFiles);
    return () => {
      voiceAppCommandBus.unregisterAppCommand('openFilePicker');
      voiceAppCommandBus.unregisterAppCommand('getSelectedFiles');
    };
  }, [selectedFiles, open]);

  const removeFile = useCallback(
    async (index: number) => {
      const removedItem = fileItems[index];
      if (!removedItem) return;
      const removedName = itemName(removedItem);
      if (removedItem.documentId) {
        try {
          await trpClient.deleteDocument(removedItem.documentId);
        } catch (err) {
          console.error('Failed to delete document on backend:', err);
        }
      }
      const newItems = fileItems.filter((_, i) => i !== index);
      setFileItems(newItems);
      const newFiles = newItems.map(i => i.file || ({ name: itemName(i), size: itemSize(i), type: '' } as any));
      voiceAppCommandBus.updateContext('uploadedFiles', newFiles);
      voiceAppCommandBus.sendVoiceFeedback('fileRemoved', { removedFile: removedName, remainingCount: newFiles.length });
    },
    [fileItems]
  );

  const submit = useCallback(async (): Promise<boolean> => {
    if (fileItems.length === 0) return true;
    const validItems = fileItems.filter(i => i.status === 'uploaded' && i.documentId);
    if (validItems.length === 0) {
      setExtractionError('Please wait for files to finish uploading or upload valid files.');
      return false;
    }
    setIsExtracting(true);
    setExtractionError(null);
    setExtractionFailed(false);
    try {
      const documentIds = validItems.map(i => i.documentId!);
      const extractedData = await trpClient.analyzeBOM(rfq.rfqId, documentIds);
      dispatch(setUploadedFiles(selectedFiles.map(f => ({ name: f.name, size: f.size, type: f.type }))));
      dispatch(setAnalysisData(extractedData));
      const componentCount = extractedData.components?.length || 0;
      setSessionComponentsFound(componentCount);
      const needsAttention = extractedData.completeness?.incomplete?.length ?? 0;
      setExtraction({ components: componentCount, needsAttention });
      recordAction('extract', robbieActionText.extracted(componentCount, needsAttention));
      showToast(extractionDoneToast(extractedData.components || [], validItems.map(itemName)));
      try {
        voiceAppCommandBus.sendVoiceFeedback('documentsExtracted', {
          filesCount: selectedFiles.length,
          fileNames: selectedFiles.map(f => f.name),
          componentCount,
          documentTypes: extractedData.documentTypes || [],
          success: true,
        });
      } catch (voiceError) {
        console.warn('Voice feedback failed, but extraction succeeded:', voiceError);
      }
      setIsExtracting(false);
      return true;
    } catch (error: any) {
      const errorMessage = error.response?.data?.error?.message
        || error.response?.data?.error
        || error.message
        || 'Failed to extract documents';
      setExtractionError(typeof errorMessage === 'string' ? errorMessage : 'Failed to extract documents');
      setExtractionFailed(true);
      voiceAppCommandBus.sendVoiceFeedback('extractionFailed', { error: errorMessage });
      setIsExtracting(false);
      return false;
    }
  }, [fileItems, selectedFiles, rfq.rfqId, dispatch, recordAction, showToast]);

  // Rows for the panel. Today extraction runs across ALL documents on
  // Continue; the resulting component count is shown on the BOM-shaped row
  // (first spreadsheet, else first uploaded file) — per-file counts arrive
  // with the later cross-referencing capability.
  const componentsFound = sessionComponentsFound || rfq.analysisData?.components?.length || 0;
  const rows: UploadRow[] = useMemo(() => {
    let bomIndex = -1;
    if (componentsFound > 0) {
      bomIndex = fileItems.findIndex(i => i.status === 'uploaded' && isBomLike(itemName(i)));
      if (bomIndex === -1) bomIndex = fileItems.findIndex(i => i.status === 'uploaded');
    }
    return fileItems.map((item, i) => {
      const base = { name: itemName(item), size: itemSize(item) };
      if (item.status === 'error') return { ...base, status: 'failed' as const, error: item.error };
      if (item.status === 'uploading') return { ...base, status: 'uploading' as const, progress: item.progress ?? 0 };
      if (i === bomIndex) return { ...base, status: 'extracted' as const, componentsFound };
      return { ...base, status: 'uploaded' as const };
    });
  }, [fileItems, componentsFound]);

  return {
    rows,
    dropzone: { rootProps: getRootProps(), inputProps: getInputProps(), isDragActive },
    openPicker: open,
    removeFile,
    submit,
    isExtracting,
    extractionError,
    extractionFailed,
    dismissFailure: () => setExtractionFailed(false),
    /** The files Continue reads: uploaded ones only. */
    readyNames: fileItems.filter((i) => i.status === 'uploaded' && i.documentId).map(itemName),
    pricing,
    extraction,
    hasUploading: fileItems.some(i => i.status === 'uploading'),
  };
};

export default useUploadStep;
